import { useState, useEffect } from 'react';
import { Modal, Col, Row, Form, Input, message, Spin, Button } from 'antd';
import { createCostCenter, getCostCenter, updateCostCenter } from '../../../services/general';
import { getCookie } from "../../../services/cookies";
import { PhoneInput } from 'cmt-ui-kit';
import { useTranslation } from 'react-i18next';

const CostCenterModal = ({
  costCenterModal,
  setCostCenterModal,
  addCostCenter,
  reservationForm,
  companyId
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState({ submit: false, fetch: false });
  const countryCode = getCookie('country_code');
  const { t } = useTranslation();

  const closeModal = () => {
    form.resetFields();
    setCostCenterModal({ ...costCenterModal, visible: false });
  }

  const fetchCostCenter = async () => {
    setLoading({ ...loading, fetch: true });
    const id = reservationForm.getFieldValue('cost_center_id');
    const response = await getCostCenter(id);
    form.setFieldsValue({
      'id': response.id,
      'active': response.active,
      'companyId': response.company_id,
      'name': response.name,
      'code': response.code,
      'manager': response.manager,
      'email': response.email,
      'phone': PhoneInput.formatPhone(response.phone, countryCode),
    })
    setLoading({ ...loading, fetch: false });
  }

  useEffect(() => {
    if (costCenterModal && form) {
      form.resetFields();
      if (costCenterModal.type === 'edit') {
        fetchCostCenter();
      }
    }
  }, [costCenterModal.visible])

  const submit = () => {
    let responsePromise;

    form
      .validateFields()
      .then(() => {
        setLoading({ ...loading, submit: true });
        const values = form.getFieldsValue();

        const params = {
          active: true,
          company_id: companyId,
          name: values.name,
          code: values.code,
          manager: values.manager,
          email: values.email,
          phone: values.phone.includes('+') ? values.phone : `+${values.phone}`,
        };

        if (values.id) {
          responsePromise = updateCostCenter(values.id, params);
        } else {
          responsePromise = createCostCenter(params);
        }

        responsePromise.then(response => {
          if (response.status === "error") {
            message.error(Object.values(response.data)[0]);
          } else {
            const data = response;

            addCostCenter({
              code: data.code,
              id: data.id
            });

            reservationForm.setFieldsValue({
              cost_center_id: data.id
            });

            message.success({
              content: `Centro de costo ${costCenterModal.type === 'edit' ? 'actualizado' : 'creado'} exitosamente`,
              style: {
                marginTop: "35vh",
              },
            });
          }
        })
          .catch(err => {
            message.error({
              content: 'Hemos tenido un error al crear el centro de costo',
              style: {
                marginTop: "35vh",
              },
            });
          })
          .finally(err => {
            form.resetFields();
            setLoading({ ...loading, submit: false });
            setCostCenterModal({ ...costCenterModal, visible: false });
          });
      })
  }

  return (
    <Modal
      forceRender
      className="creation-modal"
      open={costCenterModal.visible}
      onCancel={closeModal}
      footer={false}
      closable
    >
      <Spin spinning={loading.fetch}>
        <Form form={form} layout="vertical">
          <Row>
            <Col lg={24} xs={24}>
              <Row gutter={12}>
                <Col lg={24} xs={24}>
                  <h1 className='text-center mb-20'>
                    {costCenterModal.type === 'create' ? t('reservations.costCenterModal.newCostCenterTitle') : t('reservations.costCenterModal.editCostCenterTitle')}
                  </h1>
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item name="id" hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  <Form.Item name="companyId" hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  <Form.Item
                    name="name"
                    label={t('reservations.costCenterModal.nameLabel')}
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Input className="cmt-input" placeholder='Ingresar nombre' />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="code"
                    label={t('reservations.costCenterModal.codeLabel')}
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Input className="cmt-input" placeholder='Ingresar código' />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="manager"
                    label={t('reservations.costCenterModal.managerLabel')}
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Input className="cmt-input" placeholder='Ingresar nombre encargado' />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                  <Form.Item
                    name="email"
                    label={t('reservations.costCenterModal.emailLabel')}
                    rules={[
                      { required: true, message: 'Este campo es requerido' },
                      { type: 'email', message: 'El email es inválido' }
                    ]}
                  >
                    <Input className="cmt-input" type="email" placeholder='Ingresar email' />
                  </Form.Item>
                </Col>
                <Col lg={12} xs={24}>
                  <PhoneInput.ReactPhone
                    countryCode={countryCode}
                    form={form}
                    labelText='Teléfono'
                    optional={true}
                  />
                </Col>
                <Col lg={24} xs={24} className="mt-20">
                  <Row justify='center' gutter={[15, 15]}>
                    <Col lg={8} xs={24}>
                      <Button
                        disabled={loading.submit}
                        className='cmt-button primary outline'
                        type='primary'
                        onClick={closeModal}
                      >
                        Cancelar
                      </Button>
                    </Col>
                    <Col lg={12} xs={24}>
                      <Button
                        loading={loading.submit}
                        disabled={loading.submit}
                        className='cmt-button primary'
                        type='primary'
                        onClick={submit}
                      >
                        {costCenterModal.type === 'create' ? t('reservations.costCenterModal.createCostCenterTitle') : t('reservations.costCenterModal.updateCostCenterTitle')}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  )
}

export default CostCenterModal;
